import { useEffect, useRef, useState } from "preact/hooks";
import { Button, Celebration, Counter } from "../components";
import type { SessionType } from "../engine/session";
import { DailyCard } from "./stages/DailyCard";
import { Cleared } from "./stages/Cleared";
import { Grade } from "./stages/Grade";
import { LeadIn } from "./stages/LeadIn";
import { MicConsent } from "./stages/MicConsent";
import { Pass } from "./stages/Pass";
import { Recall } from "./stages/Recall";
import { Rhythm } from "./stages/Rhythm";
import { SessionCard } from "./stages/SessionCard";
import { Sustain } from "./stages/Sustain";
import { cardContent, labelForItem, useSession } from "./useSession";

type BoothProps = { type: SessionType; onExit: () => void };

export function Booth({ type, onExit }: BoothProps) {
  const session = useSession(type, onExit);
  const [confirmExit, setConfirmExit] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    rootRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setConfirmExit(true);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div class="booth u-scanlines u-bloom" role="dialog" aria-modal="true" aria-label="Training session" tabIndex={-1} ref={rootRef}>
      {session.phase !== "summary" && session.phase !== "celebration" && (
        <div class="booth-chrome">
          <div class="booth-progress">
            <div
              class="booth-progress-fill"
              style={{ width: `${(session.itemIndex / Math.max(1, session.items.length)) * 100}%` }}
            />
          </div>
          <span class="mono booth-step">
            {session.itemIndex + 1} / {session.items.length}
          </span>
          <Counter value={session.clarityEarned} class="booth-clarity" />
          <span class="visually-hidden" aria-live="polite">
            {session.clarityEarned} Clarity earned so far
          </span>
          <button class="booth-exit" type="button" aria-label="Exit session" onClick={() => setConfirmExit(true)}>
            ✕
          </button>
        </div>
      )}

      <div class="booth-stage">{renderStage(session)}</div>

      {confirmExit && (
        <div class="sheet-backdrop" onClick={() => setConfirmExit(false)}>
          <div class="sheet" role="dialog" aria-modal="true" aria-label="Save and finish here?" onClick={(e) => e.stopPropagation()}>
            <p class="display" style={{ fontSize: "var(--t-title)", marginBottom: 8 }}>
              Save and finish here?
            </p>
            <p style={{ color: "var(--fg-muted)", marginBottom: 16 }}>
              Everything you've earned so far is already saved.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <Button variant="ghost" onClick={() => setConfirmExit(false)}>
                Keep going
              </Button>
              <Button onClick={session.exit}>Exit</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function renderStage(session: ReturnType<typeof useSession>) {
  if (session.phase === "mic-consent") return <MicConsent onEnable={session.enableMic} onSkip={session.skipMic} />;
  if (session.phase === "lead-in") return <LeadIn onDone={session.afterLeadIn} />;
  if (session.phase === "daily-card") return <DailyCard id={session.dailyCardId} onDone={session.afterDailyCard} />;
  if (session.phase === "celebration" && session.headline) {
    return <Celebration reward={session.headline} onDone={session.afterCelebration} />;
  }
  if (session.phase === "summary") {
    return (
      <SessionCard
        clarityEarned={session.clarityEarned}
        summary={session.summary}
        rewards={session.rewards}
        waveform={session.waveform}
        onDone={session.exit}
      />
    );
  }

  const item = session.currentItem;
  if (!item) return null;

  if (session.itemPhase === "cleared") {
    return <Cleared label={labelForItem(item)} onDone={session.advance} />;
  }

  if (item.kind === "drill") {
    if (item.drillId === "syllable") {
      return (
        <Rhythm onDone={session.completeDrillItem} mic={session.mic} micState={session.micState} micThreshold={session.micThreshold} />
      );
    }
    return (
      <Sustain
        drillId={item.drillId}
        onDone={session.completeDrillItem}
        mic={session.mic}
        micState={session.micState}
        micThreshold={session.micThreshold}
      />
    );
  }

  const content = cardContent(item.cardId)!;
  if (session.itemPhase === "grade") return <Grade onGrade={session.grade} suggested={session.suggestedGrade} />;

  if (item.kind === "twister") {
    return (
      <Pass
        content={content}
        wpm={content.targetWpm[item.tempo]}
        kind="twister"
        onDone={session.completeCardStage}
        mic={session.mic}
        micState={session.micState}
        micThreshold={session.micThreshold}
      />
    );
  }
  if (item.mode === "rep") {
    return (
      <Pass
        content={content}
        wpm={content.targetWpm.slow}
        kind="phrase"
        onDone={session.completeCardStage}
        mic={session.mic}
        micState={session.micState}
        micThreshold={session.micThreshold}
      />
    );
  }
  return <Recall content={content} onDone={() => session.completeCardStage()} />;
}
